import React from 'react';
import { Card, FormField, Input } from '../ui';
import { ToggleRow } from './ToggleRow';

type PublishingSettingsCardProps = {
  workspaceName: string;
  setWorkspaceName: React.Dispatch<React.SetStateAction<string>>;
  markDirty: (_field: string) => void;
  autoApprove: boolean;
  setAutoApprove: React.Dispatch<React.SetStateAction<boolean>>;
  autoPublish: boolean;
  canAutoPublish: boolean;
  setAutoPublish: React.Dispatch<React.SetStateAction<boolean>>;
  requireFirstManual: boolean;
  setRequireFirstManual: React.Dispatch<React.SetStateAction<boolean>>;
  schedulerEnabled: boolean;
  setSchedulerEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  daysBeforeItem: string;
  setDaysBeforeItem: React.Dispatch<React.SetStateAction<string>>;
  daysBeforeMedia: string;
  setDaysBeforeMedia: React.Dispatch<React.SetStateAction<string>>;
};

export function PublishingSettingsCard({
  workspaceName,
  setWorkspaceName,
  markDirty,
  autoApprove,
  setAutoApprove,
  autoPublish,
  canAutoPublish,
  setAutoPublish,
  requireFirstManual,
  setRequireFirstManual,
  schedulerEnabled,
  setSchedulerEnabled,
  daysBeforeItem,
  setDaysBeforeItem,
  daysBeforeMedia,
  setDaysBeforeMedia,
}: PublishingSettingsCardProps) {
  return (
    <>
      <Card elevation="raised">
        <Card.Header>
          <Card.Title subtitle="General workspace configuration.">
            <span className="settings-section-icon">🏠</span> Workspace
          </Card.Title>
        </Card.Header>
        <Card.Body className="settings-card-body">
          <FormField
            label="Workspace Name"
            htmlFor="ws-name"
            hint="The display name for your workspace."
          >
            <Input
              id="ws-name"
              value={workspaceName}
              onChange={(e) => {
                setWorkspaceName(e.target.value);
                markDirty('name');
              }}
              placeholder="My Workspace"
            />
          </FormField>
        </Card.Body>
      </Card>

      <Card elevation="raised">
        <Card.Header>
          <Card.Title subtitle="Control how content is approved and published.">
            <span className="settings-section-icon">🚀</span> Publishing
          </Card.Title>
        </Card.Header>
        <Card.Body className="settings-card-body">
          <ToggleRow
            label="Auto-Approve Content"
            description="AI creates and approves content automatically — no manual review required."
            checked={autoApprove}
            onChange={(v) => {
              setAutoApprove(v);
              markDirty('autoApprove');
            }}
          />
          <ToggleRow
            label="Auto-Publish"
            description={
              canAutoPublish
                ? 'Conistra posts directly to Instagram without asking you first.'
                : '🔒 Auto-publish requires Creator plan or higher.'
            }
            checked={autoPublish && canAutoPublish}
            onChange={(v) => {
              if (canAutoPublish) {
                setAutoPublish(v);
                markDirty('autoPublish');
              }
            }}
            disabled={!canAutoPublish}
          />
          <ToggleRow
            label="Require First Manual Approval"
            description="The very first post of a new plan always requires your sign-off, even with auto-approve on."
            checked={requireFirstManual}
            onChange={(v) => {
              setRequireFirstManual(v);
              markDirty('requireFirstManual');
            }}
          />
        </Card.Body>
      </Card>

      <Card elevation="raised">
        <Card.Header>
          <Card.Title subtitle="Configure when and how the AI scheduler prepares content.">
            <span className="settings-section-icon">📅</span> Scheduler
          </Card.Title>
        </Card.Header>
        <Card.Body className="settings-card-body">
          <ToggleRow
            label="Smart Scheduler"
            description="Automatically queues creation and publishing tasks for upcoming posts."
            checked={schedulerEnabled}
            onChange={(v) => {
              setSchedulerEnabled(v);
              markDirty('schedulerEnabled');
            }}
          />

          <div className="settings-number-row">
            <FormField
              label="Days Before Item"
              htmlFor="days-item"
              hint="How many days before a publish date to start writing the post."
            >
              <Input
                id="days-item"
                type="number"
                min={2}
                max={30}
                value={daysBeforeItem}
                onChange={(e) => {
                  setDaysBeforeItem(e.target.value);
                  markDirty('daysBeforeItem');
                }}
              />
            </FormField>
            <FormField
              label="Days Before Media"
              htmlFor="days-media"
              hint="How many days before a publish date to generate the image/video."
            >
              <Input
                id="days-media"
                type="number"
                min={1}
                max={29}
                value={daysBeforeMedia}
                onChange={(e) => {
                  setDaysBeforeMedia(e.target.value);
                  markDirty('daysBeforeMedia');
                }}
              />
            </FormField>
          </div>
          <p className="settings-hint-text">
            "Days before item" must be greater than "Days before media".
          </p>
        </Card.Body>
      </Card>
    </>
  );
}
